/*
 * Exercise A screen — FR-EXA-02 (#17).
 *
 * Renders one Exercise A question (a target rune as a hero card + up to 4
 * Latin option tiles). On answer:
 *   - records the user's selection (closure-local `answered` flag),
 *   - marks the correct tile with `.option-tile--correct`,
 *   - marks the chosen tile with `.option-tile--wrong` if incorrect,
 *   - disables every option tile (no answer changes),
 *   - plays the appropriate SFX via src/data/audio.ts (M5 stub today),
 *   - emits the FR-ANALYTICS-01 answer event,
 *   - reveals an explicit Next control (no auto-advance — FR-NAV-02).
 *
 * The Exit ("Back to levels") button is always visible (FR-NAV-02 carry-
 * over from the placeholder). The screen is keyboard-accessible: 1–4
 * select options directly; Up/Down/Left/Right move a focus ring across
 * the tiles and Enter selects the focused one.
 *
 * ---------------------------------------------------------------------
 * Scope boundaries:
 *
 * - Target picking is naive (uniform random per render) — FR-LOGIC-02
 *   (#23, M3) owns proper session composition (every rune appears once,
 *   75/25 current-level vs. review mix). Today we just pick a single
 *   target from the level's getRunesForLevel() set.
 *
 * - Audio is the no-op stub in src/data/audio.ts. FR-AUDIO-01 (M5)
 *   replaces those bodies with real Audio() calls; the import boundary
 *   here stays put.
 *
 * - State across questions doesn't exist yet — every Next click is a
 *   navigate() back to this route, which re-runs render() and picks a
 *   fresh target. Scoring/streaks are M3.
 *
 * ---------------------------------------------------------------------
 * Router-dedupe quirk (Next button):
 *
 * src/router.ts dedupes by `id + paramsKey` and early-returns if both
 * match. Without a hack, clicking Next from exercise-a?n=3 → navigate
 * the same route → no re-render. We work around it by tacking a
 * monotonically-increasing `q` param onto the Next-button URL (Date.now()
 * works fine; it just needs to differ from the current paramsKey). The
 * screen ignores params.q — its only purpose is to invalidate the dedupe.
 *
 * ---------------------------------------------------------------------
 * Keyboard listener lifecycle:
 *
 * The router uses mount.replaceChildren() to swap screens; there's no
 * formal unmount hook. A naked document-level keydown listener would
 * outlive the rendered DOM (and accumulate one stale listener per
 * navigation). The brief explicitly says we can accept that leak for an
 * MVP, but in practice unit tests share a single `document` across cases
 * and stale listeners fire on every keydown, contaminating test state.
 *
 * Fix: a MutationObserver watches the screen's mount parent and aborts
 * the keydown listener (via AbortController.abort) as soon as the
 * section element is removed from the DOM — which happens on the next
 * route change's `mount.replaceChildren()`. The observer disconnects
 * itself in the same callback. Net effect: at most one keydown listener
 * is registered at any time, regardless of how many times Exercise A is
 * rendered, and tests don't need any custom cleanup hooks.
 *
 * If/when the router gains a real unmount API, this can collapse to a
 * single `controller.abort()` call in that hook and the observer can go.
 */

import { navigate } from '../router';
import { getCorpus } from '../data/csv';
import { getRunesForLevel } from '../data/level-runes';
import { generateExerciseA, type ExerciseAQuestion } from '../data/exercise-a';
import { onAnswer } from '../data/analytics';
import { playCorrect, playIncorrect } from '../data/audio';

export function renderExerciseA(mount: HTMLElement, params?: Record<string, string>): void {
  const level = parseLevel(params?.['n']);

  const section = document.createElement('section');
  section.dataset.testid = 'exercise-a';
  if (level !== null) section.dataset.level = String(level);

  // Invalid level → "unavailable" branch with just an exit button.
  if (level === null) {
    section.appendChild(renderUnavailable('Pick a level from the home screen to begin.'));
    section.appendChild(renderExitButton());
    mount.appendChild(section);
    return;
  }

  // Naive target picking — M3 (FR-LOGIC-02 / #23) will replace this with a
  // proper session runner. For now: random rune from the level's word set.
  const corpus = getCorpus();
  const runes = getRunesForLevel(corpus, level);
  if (runes.length === 0) {
    section.appendChild(renderUnavailable(`No runes are configured for Level ${level} yet.`));
    section.appendChild(renderExitButton());
    mount.appendChild(section);
    return;
  }

  const targetIndex = Math.floor(Math.random() * runes.length);
  // Non-null assertion is safe: 0 <= targetIndex < runes.length and we've
  // already early-returned on the empty case. `noUncheckedIndexedAccess`
  // forces the assertion.
  const target = runes[targetIndex]!;
  const question = generateExerciseA(target, corpus, level);

  // ---- Closure-local state ----
  // The render fn is called once per route entry; question + answered flag
  // live in this closure for the lifetime of the rendered DOM.
  let answered = false;
  let focusedIndex = 0;
  const tiles: HTMLButtonElement[] = [];
  let nextButton: HTMLButtonElement | null = null;

  function handleAnswer(idx: number): void {
    if (answered) return;
    if (idx < 0 || idx >= question.options.length) return;
    answered = true;

    const wasCorrect = idx === question.correctIndex;

    // Always mark the actual correct answer; mark the chosen tile wrong if
    // it didn't match. Order matters: if the user picked correctly, both
    // "correct" branches resolve to the same tile, so we only add the
    // `--correct` class once.
    const correctTile = tiles[question.correctIndex];
    if (correctTile) correctTile.classList.add('option-tile--correct');
    if (!wasCorrect) {
      const chosenTile = tiles[idx];
      if (chosenTile) chosenTile.classList.add('option-tile--wrong');
    }

    // Disable all tiles so further clicks are inert (the closure-local
    // `answered` guard also catches keyboard input).
    for (const tile of tiles) {
      tile.disabled = true;
    }

    // Audio + analytics. Audio is a no-op stub (FR-AUDIO-01 / M5); analytics
    // is the real seam from FR-ANALYTICS-01.
    if (wasCorrect) {
      playCorrect();
    } else {
      playIncorrect();
    }
    // Use question.target.latin (not the outer `target`) so the analytics
    // payload always matches the rune the user actually saw, even if the
    // generator someday substitutes a different target.
    onAnswer(question.target.latin, 'A', wasCorrect);

    // Reveal Next (it was hidden until the user committed to an answer).
    if (nextButton) {
      nextButton.hidden = false;
      // Move focus to Next so keyboard users can press Enter to advance.
      nextButton.focus();
    }
  }

  // ---- Hero card with the target rune ----
  const heroCard = document.createElement('article');
  heroCard.className = 'card card--elevated card--hero';
  heroCard.dataset.testid = 'exercise-a-hero';
  heroCard.style.textAlign = 'center';

  const prompt = document.createElement('div');
  prompt.dataset.testid = 'exercise-a-prompt';
  prompt.style.color = 'var(--color-text-warm)';
  prompt.style.fontSize = 'var(--font-size-body-s)';
  prompt.style.textTransform = 'uppercase';
  prompt.style.letterSpacing = '0.1em';
  prompt.textContent = 'Which rune is this?';
  heroCard.appendChild(prompt);

  const focalGlyph = Array.from(question.target.rune)[0] ?? question.target.rune;
  const glyph = document.createElement('div');
  glyph.className = 'rune rune--hero';
  glyph.dataset.testid = 'exercise-a-rune';
  glyph.style.marginTop = 'var(--space-4)';
  glyph.textContent = focalGlyph;
  heroCard.appendChild(glyph);

  section.appendChild(heroCard);

  // ---- Option tiles ----
  const optionList = document.createElement('div');
  optionList.dataset.testid = 'exercise-a-options';
  optionList.style.display = 'grid';
  optionList.style.gap = 'var(--space-3)';
  optionList.style.marginTop = 'var(--space-6)';

  question.options.forEach((option, idx) => {
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'option-tile';
    tile.dataset.testid = `exercise-a-option-${idx}`;
    tile.dataset.optionIndex = String(idx);
    // Minimum touch-target (44×44) is met by the existing .option-tile
    // padding + body-l font-size; min-height here is belt-and-braces in
    // case the design tokens drift.
    tile.style.minHeight = '44px';

    const key = document.createElement('span');
    key.className = 'option-tile__key';
    key.textContent = String(idx + 1);
    tile.appendChild(key);

    const label = document.createElement('span');
    label.textContent = option.latin;
    tile.appendChild(label);

    tile.addEventListener('click', () => {
      handleAnswer(idx);
    });

    tiles.push(tile);
    optionList.appendChild(tile);
  });

  section.appendChild(optionList);

  // ---- Action row: Next (hidden until answered) + Exit ----
  const actions = document.createElement('div');
  actions.dataset.testid = 'exercise-a-actions';
  actions.style.display = 'flex';
  actions.style.gap = 'var(--space-3)';
  actions.style.marginTop = 'var(--space-6)';

  nextButton = document.createElement('button');
  nextButton.type = 'button';
  nextButton.className = 'btn btn--primary';
  nextButton.dataset.testid = 'exercise-a-next';
  nextButton.textContent = 'Next →';
  nextButton.hidden = true; // becomes visible after handleAnswer fires
  nextButton.addEventListener('click', () => {
    // The `q=<timestamp>` cache-buster is the router-dedupe workaround
    // (see top-of-file comment). Without it, navigate('exercise-a?n=N')
    // from this same route would early-return and no re-render fires.
    const nextToken = Date.now();
    navigate(`exercise-a?n=${level}&q=${nextToken}`);
  });
  actions.appendChild(nextButton);

  actions.appendChild(renderExitButton());

  section.appendChild(actions);

  mount.appendChild(section);

  // ---- Keyboard handling + auto-cleanup ----
  // See top-of-file comment on the listener lifecycle. The MutationObserver
  // below watches the document root with `subtree: true` and aborts the
  // keydown listener as soon as our `section` is no longer connected:
  //   - Production: the router calls mount.replaceChildren() on navigation,
  //     which removes `section`.
  //   - Tests: typically clear document.body, which removes the mount and
  //     transitively `section`.
  // Either way, `section.isConnected` flips to false and we abort.
  const controller = new AbortController();
  const observer = new MutationObserver(() => {
    if (!section.isConnected) {
      controller.abort();
      observer.disconnect();
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener(
    'keydown',
    (event) => {
      if (answered) return;
      // 1-4 → direct select
      if (event.key >= '1' && event.key <= '9') {
        const idx = Number.parseInt(event.key, 10) - 1;
        if (idx >= 0 && idx < tiles.length) {
          event.preventDefault();
          handleAnswer(idx);
        }
        return;
      }
      // Arrow keys → move focus ring
      if (
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowRight'
      ) {
        event.preventDefault();
        const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
        focusedIndex = clamp(focusedIndex + delta, 0, tiles.length - 1);
        const tile = tiles[focusedIndex];
        if (tile) tile.focus();
        return;
      }
      // Enter → commit focused
      if (event.key === 'Enter') {
        // Only treat Enter as "answer" when an option tile is the active
        // element — otherwise the Exit/Next buttons would steal it.
        const active = document.activeElement;
        const tileIdx = tiles.findIndex((t) => t === active);
        if (tileIdx >= 0) {
          event.preventDefault();
          handleAnswer(tileIdx);
        }
      }
    },
    { signal: controller.signal },
  );
}

function parseLevel(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  // Require digit-only input: Number.parseInt would otherwise silently
  // accept "2abc" → 2 and "2.5" → 2, so a hand-edited URL like
  // ?n=2.5 would resolve to level 2 with no signal that the param was
  // malformed. parseLevel in level-intro.ts has the same shape — fix it
  // there in a follow-up if needed.
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number.parseInt(raw, 10);
  if (parsed < 1 || parsed > 8) return null;
  return parsed;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function renderUnavailable(message: string): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';
  card.dataset.testid = 'exercise-a-unavailable';

  const heading = document.createElement('h1');
  heading.textContent = 'No exercise available';
  card.appendChild(heading);

  const body = document.createElement('p');
  body.style.marginTop = 'var(--space-3)';
  body.style.color = 'var(--color-text-warm)';
  body.textContent = message;
  card.appendChild(body);

  return card;
}

function renderExitButton(): HTMLButtonElement {
  // FR-NAV-02 exit affordance: users can always escape back to the level
  // grid, regardless of whether they've answered the current question.
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'btn btn--secondary';
  back.dataset.testid = 'exercise-a-exit';
  back.textContent = 'Back to levels';
  back.addEventListener('click', () => {
    navigate('');
  });
  return back;
}

// Re-export the question shape so test files can type-check synthetic
// fixtures without reaching into src/data.
export type { ExerciseAQuestion };
